import { Component } from "@angular/core";
import { MaterialType } from "@enums/materialType.enum";
import { BilletPlanInputs, BilletPlanResult, DEFAULT_BILLET_PLAN_INPUTS, MRR_LIBRARY, MrrLibraryEntry, computeBilletPlan, densityKgM3ForMaterialType, mrrFromFeedAndEngagement } from "@helpers/billetPlanning";
import { GetPropertiesFromEnum } from "@helpers/getPropertiesFromEnum";
import { AdminHeaderButton } from "@interfaces/adminHeaderButton.interface";

// Per-stage view model. Holds the feed-mode inputs the helper doesn't need (it only stores the derived
// MRR) so a stage can be entered as a bare MRR or as feedrate + cut cross-section.
interface RoughStageVm {
	ae: number;
	ap: number;
	factor: number;
	feed: number;
	fixedHrs: number;
	mrr: number;
	rateMode: "mrr" | "feed";
	share: number;
}

// Default finish area in mm² (the workbook sample, matching DEFAULT_BILLET_PLAN_INPUTS.finishAreaM2).
const DEFAULT_AREA_MM2 = 323964.5;

function primaryStage(): RoughStageVm {
	return { ae: 19, ap: 0.6, factor: 1, feed: 4000, fixedHrs: 0, mrr: 45.6, rateMode: "feed", share: 0.96 };
}

// Small-cutter rest-rough for tight areas: low effective factor because retracts, repositioning and
// partial engagement mean the real removal rate is a fraction of the theoretical ap x ae x feed.
function secondaryStage(): RoughStageVm {
	return { ae: 5, ap: 0.3, factor: 0.4, feed: 1300, fixedHrs: 0, mrr: 1.95, rateMode: "feed", share: 0.03 };
}

@Component({
	moduleId: module.id,
	selector: "billetPlanner",
	styleUrls: ["billetPlanner.css"],
	templateUrl: "billetPlanner.html"
})
export class BilletPlannerAdminComponent {
	areaUnit: "m2" | "mm2" = "mm2";
	combinedShare = 0.9;
	finishAreaInput = DEFAULT_AREA_MM2;
	headerPrimaryButtons: AdminHeaderButton[] = [];
	headerTertiaryButtons: AdminHeaderButton[] = [];
	inputs: BilletPlanInputs = { ...DEFAULT_BILLET_PLAN_INPUTS };
	materialOptions = GetPropertiesFromEnum(MaterialType).filter((m) => densityKgM3ForMaterialType(m.id) > 0);
	materialType: MaterialType | null = MaterialType.Steel;
	mrrLibrary: MrrLibraryEntry[] = MRR_LIBRARY;
	projectName = "Example large BIW form block";
	result: BilletPlanResult = computeBilletPlan(DEFAULT_BILLET_PLAN_INPUTS);
	roughStages: RoughStageVm[] = [primaryStage(), secondaryStage()];
	title = "Billet Estimator";

	constructor() {
		this.syncRoughing();
	}

	applyFinishingPreset(entry: MrrLibraryEntry) {
		if (entry && entry.finishFeedStart) {
			this.inputs.finishFeedMmMin = entry.finishFeedStart;
			this.recompute();
		}
	}

	applyRoughingPreset(entry: MrrLibraryEntry) {
		if (entry && entry.roughMrrStart && this.roughStages.length) {
			this.roughStages[0].rateMode = "mrr";
			this.roughStages[0].mrr = entry.roughMrrStart;
			this.syncRoughing();
		}
	}

	areaChanged() {
		this.inputs.finishAreaM2 = this.areaUnit === "mm2" ? this.finishAreaInput / 1000000 : this.finishAreaInput;
		this.recompute();
	}

	materialChanged() {
		if (this.materialType) {
			const density = densityKgM3ForMaterialType(this.materialType);
			if (density > 0) {
				this.inputs.densityKgM3 = density;
				this.recompute();
			}
		}
	}

	recompute() {
		this.result = computeBilletPlan(this.inputs);
	}

	resetToDefaults() {
		this.inputs = { ...DEFAULT_BILLET_PLAN_INPUTS };
		this.materialType = MaterialType.Steel;
		this.areaUnit = "mm2";
		this.finishAreaInput = DEFAULT_AREA_MM2;
		this.roughStages = [primaryStage(), secondaryStage()];
		this.syncRoughing();
	}

	setAreaUnit(unit: "m2" | "mm2") {
		if (unit === this.areaUnit) { return; }
		// convert the displayed number so the physical area is unchanged
		this.finishAreaInput = unit === "mm2" ? this.finishAreaInput * 1000000 : this.finishAreaInput / 1000000;
		this.areaUnit = unit;
	}

	setStageMode(stage: RoughStageVm, mode: "mrr" | "feed") {
		stage.rateMode = mode;
		this.syncRoughing();
	}

	syncRoughing() {
		this.roughStages.forEach((s) => {
			if (s.rateMode === "feed") { s.mrr = mrrFromFeedAndEngagement(s.feed, s.ap, s.ae); }
		});
		this.inputs.roughingStages = this.roughStages.map((s) => ({ factor: s.factor, fixedHrs: s.fixedHrs, mrrCm3Min: s.mrr, share: s.share }));
		this.combinedShare = this.roughStages.reduce((acc, s) => acc + s.share, 0);
		this.recompute();
	}
}
